import React, { forwardRef } from 'react';
import { ExclamationCircleIcon } from '@heroicons/react/24/outline';
import type { BaseInputProps } from './shared';
import { inputBaseClasses, inputErrorClasses } from './shared';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectInputProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'className'>, BaseInputProps {
  options: SelectOption[];
  placeholder?: string;
}

export const SelectInput = forwardRef<HTMLSelectElement, SelectInputProps>(
  (
    {
      label,
      id,
      errors = [],
      helpText,
      containerClassName = '',
      className = '',
      options,
      placeholder,
      disabled,
      required,
      ...props
    },
    ref
  ) => {
    const hasErrors = errors.length > 0;
    const inputId = id || `select-input-${Math.random().toString(36).slice(2)}`;

    const selectClasses = [
      inputBaseClasses,
      'appearance-none bg-select-token border-select-token pr-10 cursor-pointer',
      hasErrors ? inputErrorClasses : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={`space-y-token-xs ${containerClassName}`}>
        {label && (
          <label htmlFor={inputId} className="block type-caption-text font-medium text-ink">
            {label}
            {required && <span className="text-token-danger ml-1">*</span>}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={inputId}
            disabled={disabled}
            required={required}
            aria-invalid={hasErrors}
            aria-describedby={
              hasErrors ? `${inputId}-errors` : helpText ? `${inputId}-help` : undefined
            }
            aria-label={label || props['aria-label']}
            className={selectClasses}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map(opt => (
              <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </option>
            ))}
          </select>
          <div
            className="absolute inset-y-0 right-0 pr-token-sm flex items-center pointer-events-none text-muted"
            aria-hidden
          >
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </div>
        </div>
        {hasErrors && (
          <div id={`${inputId}-errors`} className="space-y-1" role="alert">
            {errors.map((error, index) => (
              <p
                key={index}
                className="type-caption-text text-token-danger flex items-center gap-1"
              >
                <ExclamationCircleIcon className="h-4 w-4 flex-shrink-0" />
                {error}
              </p>
            ))}
          </div>
        )}
        {helpText && !hasErrors && (
          <p id={`${inputId}-help`} className="type-caption-text text-muted">
            {helpText}
          </p>
        )}
      </div>
    );
  }
);

SelectInput.displayName = 'SelectInput';
